import { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, Trash2, Save, ChevronLeft, ChevronRight, Clock, Package, RefreshCcw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminMenuSchedule() {
  const { token } = useAuth();
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedules, setSchedules] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [categoryStatus, setCategoryStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [scheduleSettings, setScheduleSettings] = useState({
    pre_order_cutoff: '09:00',
    delivery_start: '12:00',
    delivery_end: '13:00'
  });
  const [saving, setSaving] = useState(false);

  // Check if menu is ready to publish (all categories complete)
  const isMenuReady = categoryStatus.every(cat => cat.status === 'ready' || cat.status === 'empty');
  const incompleteCategories = categoryStatus.filter(cat => cat.status === 'incomplete');

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getWeekDates = useCallback(() => {
    const dates = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day + 1); // Start from Monday
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  const getMonthDates = useCallback(() => {
    const dates = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Pad with previous month days
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = startPadding; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      dates.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Pad with next month days
    const endPadding = 42 - dates.length;
    for (let i = 1; i <= endPadding; i++) {
      dates.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return dates;
  }, [currentDate]);

  const fetchData = useCallback(async () => {
    try {
      // Get date range based on view mode
      let startDate, endDate;
      if (viewMode === 'week') {
        const weekDates = getWeekDates();
        startDate = formatDate(weekDates[0]);
        endDate = formatDate(weekDates[6]);
      } else {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        startDate = formatDate(new Date(year, month, 1));
        endDate = formatDate(new Date(year, month + 1, 0));
      }

      const [schedulesRes, itemsRes, categoryRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/menu/schedules?start_date=${startDate}&end_date=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/menu`),
        fetch(`${API_URL}/api/admin/menu/category-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        })
      ]);

      if (schedulesRes.ok) {
        const schedulesData = await schedulesRes.json();
        const schedulesMap = {};
        schedulesData.forEach(s => {
          schedulesMap[s.date] = s;
        });
        setSchedules(schedulesMap);
      }

      if (itemsRes.ok) {
        setMenuItems(await itemsRes.json());
      }

      if (categoryRes.ok) {
        setCategoryStatus(await categoryRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, viewMode, currentDate, getWeekDates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const openScheduleEditor = (date) => {
    const dateStr = formatDate(date);
    const existing = schedules[dateStr];
    
    if (existing) {
      setSelectedItems(existing.items || []);
      setScheduleSettings({
        pre_order_cutoff: existing.pre_order_cutoff || '09:00',
        delivery_start: existing.delivery_start || '12:00',
        delivery_end: existing.delivery_end || '13:00'
      });
    } else {
      // Default: all available items
      setSelectedItems(menuItems.filter(i => i.is_available).map(i => ({
        item_id: i.item_id,
        name: i.name,
        price: i.price,
        quantity_available: i.stock_quantity || 50
      })));
      setScheduleSettings({
        pre_order_cutoff: '09:00',
        delivery_start: '12:00',
        delivery_end: '13:00'
      });
    }
    setSelectedDate(date);
  };

  const toggleItem = (item) => {
    const exists = selectedItems.find(i => i.item_id === item.item_id);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, {
        item_id: item.item_id,
        name: item.name,
        price: item.price,
        quantity_available: item.stock_quantity || 50
      }]);
    }
  };

  const updateItemQuantity = (itemId, quantity) => {
    setSelectedItems(selectedItems.map(i => 
      i.item_id === itemId ? { ...i, quantity_available: parseInt(quantity) || 0 } : i
    ));
  };

  const saveSchedule = async () => {
    if (!selectedDate) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/menu/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          date: formatDate(selectedDate),
          items: selectedItems,
          ...scheduleSettings
        })
      });

      if (!response.ok) throw new Error('Failed to save schedule');

      toast.success(`Schedule saved for ${formatDate(selectedDate)}`);
      setSelectedDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBulkSchedule = async (dates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/menu/schedule/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          dates: dates.map(d => formatDate(d)),
          items: selectedItems,
          ...scheduleSettings
        })
      });

      if (!response.ok) throw new Error('Failed to save schedules');

      toast.success(`Schedules saved for ${dates.length} days`);
      setSelectedDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async (dateStr) => {
    const schedule = schedules[dateStr];
    if (!schedule) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/menu/schedule/${schedule.schedule_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete schedule');

      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-menu-schedule">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-secondary-900">Menu Scheduling</h1>
          <p className="text-muted-foreground">Plan and schedule menus for upcoming days</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            onClick={() => setViewMode('week')}
            size="sm"
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            onClick={() => setViewMode('month')}
            size="sm"
          >
            Month
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold">
              {viewMode === 'week' 
                ? `Week of ${formatDate(getWeekDates()[0])}`
                : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              }
            </h2>
            <Button variant="outline" size="sm" onClick={() => navigate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      {viewMode === 'week' ? (
        <div className="grid grid-cols-7 gap-4">
          {getWeekDates().map((date, idx) => {
            const dateStr = formatDate(date);
            const schedule = schedules[dateStr];
            const isToday = dateStr === formatDate(new Date());
            const isPast = date < new Date(new Date().setHours(0,0,0,0));

            return (
              <Card key={dateStr} className={`${isToday ? 'border-primary border-2' : ''} ${isPast ? 'opacity-60' : ''}`}>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex justify-between items-center">
                    <span>{dayNames[idx]}</span>
                    <span className={`text-xs ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                      {date.getDate()}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {schedule ? (
                    <div className="space-y-2">
                      <Badge variant="outline" className="text-xs">
                        {schedule.items?.length || 0} items
                      </Badge>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Cutoff: {schedule.pre_order_cutoff}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => openScheduleEditor(date)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => deleteSchedule(dateStr)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="w-full text-xs"
                      onClick={() => openScheduleEditor(date)}
                      disabled={isPast}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              {getMonthDates().map(({ date, isCurrentMonth }, idx) => {
                const dateStr = formatDate(date);
                const schedule = schedules[dateStr];
                const isToday = dateStr === formatDate(new Date());
                const isPast = date < new Date(new Date().setHours(0,0,0,0));

                return (
                  <div
                    key={dateStr}
                    className={`
                      min-h-[80px] p-2 border rounded cursor-pointer transition-colors
                      ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${isToday ? 'border-primary border-2' : 'border-gray-200'}
                      ${isPast ? 'opacity-50' : 'hover:border-primary/50'}
                    `}
                    onClick={() => !isPast && openScheduleEditor(date)}
                  >
                    <div className={`text-sm ${isToday ? 'font-bold text-primary' : isCurrentMonth ? '' : 'text-gray-400'}`}>
                      {date.getDate()}
                    </div>
                    {schedule && (
                      <div className="mt-1">
                        <Badge variant="outline" className="text-[10px] px-1">
                          {schedule.items?.length || 0}
                        </Badge>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Editor Dialog */}
      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Schedule for {selectedDate ? formatDate(selectedDate) : ''}
            </DialogTitle>
          </DialogHeader>

          {/* Publish Guardrail Warning */}
          {!isMenuReady && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200">Menu Incomplete</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Complete all menu items (images, descriptions, prices) before scheduling. Incomplete categories:
                  </p>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 space-y-1">
                    {incompleteCategories.map(cat => (
                      <li key={cat.menu_type} className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-500 rounded-full" />
                        <span className="font-medium">{cat.label}</span>
                        <span className="text-xs">({cat.items_with_images}/{cat.total_items} items ready)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {isMenuReady && (
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-sm font-medium">Menu ready to schedule</span>
              </div>
            </div>
          )}

          <div className="space-y-6 py-4">
            {/* Time Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Pre-order Cutoff</Label>
                <Input
                  type="time"
                  value={scheduleSettings.pre_order_cutoff}
                  onChange={(e) => setScheduleSettings(s => ({ ...s, pre_order_cutoff: e.target.value }))}
                />
              </div>
              <div>
                <Label>Delivery Start</Label>
                <Input
                  type="time"
                  value={scheduleSettings.delivery_start}
                  onChange={(e) => setScheduleSettings(s => ({ ...s, delivery_start: e.target.value }))}
                />
              </div>
              <div>
                <Label>Delivery End</Label>
                <Input
                  type="time"
                  value={scheduleSettings.delivery_end}
                  onChange={(e) => setScheduleSettings(s => ({ ...s, delivery_end: e.target.value }))}
                />
              </div>
            </div>

            {/* Menu Items Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Menu Items</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // Sync all available items with their current stock
                    setSelectedItems(menuItems.filter(i => i.is_available !== false).map(i => ({
                      item_id: i.item_id,
                      name: i.name,
                      price: i.price,
                      quantity_available: i.stock_quantity || 50
                    })));
                    toast.success('Synced with current menu items');
                  }}
                  data-testid="sync-menu-items"
                >
                  <RefreshCcw className="h-3 w-3 mr-1" />
                  Sync from Menu
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-3">
                {menuItems.map(item => {
                  const isSelected = selectedItems.find(i => i.item_id === item.item_id);
                  const stockLevel = item.stock_quantity || 0;
                  const isLowStock = stockLevel < 10;
                  return (
                    <div key={item.item_id} className={`flex items-center gap-3 p-2 hover:bg-gray-50 rounded ${isLowStock ? 'bg-orange-50' : ''}`}>
                      <Checkbox
                        checked={!!isSelected}
                        onCheckedChange={() => toggleItem(item)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.name}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">R{item.price?.toFixed(2)}</p>
                          <span className={`text-xs ${isLowStock ? 'text-orange-600 font-medium' : 'text-slate-400'}`}>
                            Stock: {stockLevel}
                          </span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Avail:</Label>
                          <Input
                            type="number"
                            value={isSelected.quantity_available}
                            onChange={(e) => updateItemQuantity(item.item_id, e.target.value)}
                            className="w-20 h-8"
                            min={0}
                            max={stockLevel}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedItems.length} of {menuItems.length} items selected
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setSelectedDate(null)}>
              Cancel
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={saveSchedule} 
                      disabled={saving || selectedItems.length === 0 || !isMenuReady}
                      data-testid="save-schedule-btn"
                    >
                      {!isMenuReady ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {saving ? 'Saving...' : 'Save Schedule'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!isMenuReady && (
                  <TooltipContent side="top" className="max-w-[250px]">
                    <div className="space-y-1">
                      <p className="font-semibold text-amber-500">Menu Incomplete</p>
                      <p className="text-xs">Complete all menu items before scheduling:</p>
                      <ul className="text-xs list-disc pl-4">
                        {incompleteCategories.map(cat => (
                          <li key={cat.menu_type}>{cat.label}: {cat.items_with_images}/{cat.total_items} ready</li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
